import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "aws",
  name: "AWS / クラウド",
  nameEn: "AWS & Cloud",
  seo: {
    title:
      "AWS / クラウドを学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "AWSは世界シェアNo.1のクラウドサービスです。ゆめさくエンジニアならEC2・S3・Lambda・RDSなどを実践的に学び、クラウドネイティブな開発スキルを習得できます。", // claims-ok 技術や職種の一般的な話
    url: "/columns/aws-programming-school",
  },
  hero: {
    title: "AWS / クラウドを学べる女性向けプログラミングスクール",
    englishTitle: "Learn AWS & Cloud",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "AWS / クラウド" },
  ],
  overview: {
    heading: "AWS / クラウドとは？",
    paragraphs: [
      "AWS（Amazon Web Services）は、Amazonが提供する世界最大のクラウドコンピューティングプラットフォームです。サーバー・データベース・ストレージ・AI/MLなど200以上のサービスを提供し、世界中の企業のITインフラを支えています。",
      "クラウドコンピューティングとは、自社でサーバーを持たず、インターネット経由でコンピュータ資源を利用する仕組みです。Netflix、Airbnb、NASAなどの世界的企業がAWSを採用しており、日本でも多くの企業がオンプレミスからクラウドへの移行を進めています。",
      "初期費用なし・使った分だけ課金の料金体系で、スタートアップから大企業まで幅広く活用されています。クラウド市場は年率20%以上で成長を続けており、エンジニアの需要も急増しています。",
    ],
    useCases: [
      {
        label: "Webアプリケーションのホスティング",
        description:
          "EC2やECS上にアプリケーションをデプロイし、ALBで負荷分散、CloudFrontでCDN配信を行い、高可用性・高パフォーマンスを実現します。",
      },
      {
        label: "サーバーレスアプリケーション",
        description:
          "AWS Lambdaを使えばサーバー管理不要でコードを実行できます。API Gateway + Lambda + DynamoDBでスケーラブルなAPIを低コストで構築できます。",
      },
      {
        label: "データの保存・配信",
        description:
          "S3に画像・動画・ドキュメントを保存し、CloudFrontで世界中に高速配信。RDSでリレーショナルデータベースをマネージドに運用します。",
      },
      {
        label: "監視・ログ管理",
        description:
          "CloudWatchでサーバーの稼働状況を監視し、アラートを設定。CloudTrailで操作ログを記録し、セキュリティ監査を実施します。",
      },
    ],
  },
  whyLearn: {
    heading: "AWS / クラウドを学ぶべき3つの理由",
    items: [
      {
        title: "クラウドシフトで需要が急拡大中",
        description:
          "DX推進により、オンプレミスからクラウドへの移行が加速しています。AWSスキルを持つエンジニアは慢性的に不足しており、経験1〜2年でも高い年収が期待できる売り手市場です。",
      },
      {
        title: "一つのサービスから広がる無限のキャリア",
        description:
          "AWSは200以上のサービスを提供しており、インフラ・データ分析・AI/ML・セキュリティなど、興味に応じてキャリアを広げることができます。AWS認定資格の取得でさらにキャリアアップが見込めます。",
      },
      {
        title: "個人でもすぐに始められる",
        description:
          "AWSには無料利用枠があり、個人のアカウントで実際にサービスを触りながら学習できます。物理サーバーを用意する必要がないため、自宅のパソコンからすぐにクラウド学習を始められます。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでAWS / クラウドを学ぶ",
    paragraphs: [
      "ゆめさくエンジニアではPhase 5の基礎としてAWSを学びます。それまでに開発したWebアプリケーションを実際にAWS上にデプロイし、本番環境での運用スキルを身につけます。",
      "座学だけでなく、実際のAWSアカウントでハンズオンを行うため、試験対策にもなる実践的な知識が身につきます。メンターがコスト管理もサポートするため、課金の不安なく学習を進められます。",
    ],
    topics: [
      "クラウドコンピューティングの基本概念",
      "EC2によるサーバー構築・管理",
      "S3によるファイルストレージ管理",
      "RDSによるデータベース運用",
      "Lambda・API Gatewayによるサーバーレス入門",
      "CloudFrontによるCDN配信・独自ドメイン設定",
    ],
  },
  careerPaths: {
    heading: "AWS / クラウドを活かせるキャリア",
    items: [
      {
        title: "クラウドエンジニア",
        description:
          "AWSを中心としたクラウドインフラの設計・構築・運用を担当します。企業のクラウド移行プロジェクトのリードや、既存環境の最適化・コスト削減を行います。",
        salary: "500〜900万円",
      },
      {
        title: "SRE（Site Reliability Engineer）",
        description:
          "クラウド上のシステムの信頼性を高めるため、監視設計・障害対応の自動化・キャパシティプランニングを行います。高度なクラウド知識とプログラミングスキルの両方が求められます。",
        salary: "600〜1,000万円",
      },
      {
        title: "インフラエンジニア",
        description:
          "オンプレミスとクラウドのハイブリッド環境を含むITインフラ全般の設計・構築を担当します。ネットワーク、セキュリティ、バックアップなど幅広い領域をカバーします。",
        salary: "500〜800万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "6〜12ヶ月（サービスが多すぎて迷子に）",
        luagate: "3〜4ヶ月（実務に必要なサービスから優先的に学習）",
      },
      {
        label: "挫折率",
        selfStudy: "課金の不安やサービスの多さで挫折しやすい",
        luagate: "メンターがコスト管理もサポートし安心して学習",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "チュートリアル通りにリソースを作るだけ",
        luagate: "自分のWebアプリをAWSにデプロイする実践課題",
      },
      {
        label: "質問対応",
        selfStudy: "ドキュメントが英語中心で理解に時間がかかる",
        luagate: "現役クラウドエンジニアに日本語で質問可能",
      },
      {
        label: "就職サポート",
        selfStudy: "すべて自力で進める",
        luagate: "ポートフォリオ添削・面接対策まで一貫サポート",
      },
    ],
  },
  faq: [
    {
      question: "AWSの学習に費用はかかりますか？",
      answer:
        "AWSには12ヶ月間の無料利用枠があり、EC2やS3、RDSなど主要サービスを無料で試せます。ゆめさくエンジニアの学習範囲であれば無料枠内に収まることがほとんどです。万が一の課金についてもメンターがチェックをサポートします。",
    },
    {
      question: "AWSとGoogle Cloud、Azureのどれを学ぶべきですか？",
      answer:
        "AWSは世界シェアNo.1で、求人数・学習リソース・認定資格の充実度が最も高いため、まずAWSから始めるのがおすすめです。クラウドの基本概念は共通しているため、AWS経験があれば他のクラウドへの移行もスムーズです。", // claims-ok 技術や職種の一般的な話
    },
    {
      question: "プログラミング経験なしでもAWSを学べますか？",
      answer:
        "ゆめさくエンジニアではPhase 1〜4でプログラミングの基礎を十分に身につけた後にAWSに入ります。コードを書けるようになってからインフラを学ぶため、「なぜこの設定が必要なのか」を深く理解できます。",
    },
    {
      question: "AWS認定資格は取得した方がいいですか？",
      answer:
        "はい、特にAWS Solutions Architect Associate（SAA）は転職市場で高く評価されます。ゆめさくエンジニアのカリキュラムで学ぶ内容はSAA試験の範囲と重なる部分が多く、学習後に資格取得を目指す方も多数います。",
    },
    {
      question: "AWSを学んだ後、すぐに仕事で使えますか？",
      answer:
        "ゆめさくエンジニアでは実際のAWS環境を使ったハンズオンを行うため、学習内容がそのまま業務に直結します。EC2でのサーバー構築、S3でのファイル管理、RDSでのDB運用など、入社後すぐに活かせるスキルが身につきます。",
    },
  ],
  relatedLanguages: [
    { slug: "linux", name: "Linux" },
    { slug: "docker", name: "Docker / CI-CD" },
    { slug: "terraform", name: "Terraform" },
    { slug: "aws-saa", name: "AWS SAA資格" },
  ],
};
